<template>
  <div class="predictive-search__header">
    <form
      class="predictive-search__input-field"
      role="search"
      :action="$variable('routes.search')"
      @submit.prevent="onSubmit"
    >
      <button
        class="predictive-search__icon"
        type="submit"
      >
        <icon-search />
      </button>

      <input
        ref="inputField"
        v-model.trim="input"
        class="predictive-search__input"
        type="text"
        autocomplete="off"
        tabindex="1"
        @input="handleSearch"
        @keyup.enter="onSubmit"
      >
    </form>

    <div
      v-if="query.length > 0 && responseData.total_product == 0"
      class="predictive-search__no-results"
    >
      <span
        v-text="$string(
          'search.no_results',
          { replace: { query: query } },
        )"
      />
    </div>
  </div>

  <div class="predictive-search__results-container">
    <div
      class="predictive-search__results"
    >
      <div class="predictive-search__quick-access--container">
        <p
          class="predictive-search__results-header"
          v-text="$string('search.quick_access_title')"
        />

        <ul class="predictive-search__quick-access">
          <li
            v-for="link in quickAccess"
            :key="link.handle"
          >
            <a
              :href="link.url"
              v-html="link.title"
            />
          </li>
        </ul>
      </div>

      <div class="predictive-search__products--container">
        <p
          class="predictive-search__results-header"
          v-text="$string('search.results_title')"
        />

        <ul
          v-if="products.length > 0"
          class="predictive-search__products"
        >
          <li
            v-for="(product, index) in products"
            :key="product ? product.id : `search-product-${index}`"
          >
            <a
              class="predictive-search__product-card"
              :href="product.url"
              @click="handleAnalyticsEvent(product.id.toString())"
            >
              <div class="predictive-search__product-card__image-container">
                <responsive-image
                  block-class="predictive-search__product-card"
                  :image="product.images[0]"
                  :lazy-load="true"
                  min-max="50-50"
                  ratio="1:1"
                  sizes="50px"
                />
              </div>

              <p
                class="predictive-search__product-card__title"
                v-text="product.title"
              />
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>

  <boost-api-call
    ref="boost"
    :query="query"
    :endpoint="endpoint"
    :limit="4"
    :shop="liquid.shop"
    event-type="suggest_dym"
    @boost-updated="boostUpdated"
  />

  <boost-analytics
    :analytics-event="analyticsEvent"
    type="search"
  />
</template>

<script>

/**
 * Vue: Predictive search (predictive-search)
 * -----------------------------------------------------------------------------
 * Section containing predictive search input field and results
 *
 */

import BoostAnalytics from '~global/boost-analytics/boost-analytics'
import BoostApiCall from '~global/boost-api-call/boost-api-call'
import ResponsiveImage from '~global/images/responsive-image'
import IconSearch from '~icons/misc/search.svg'

import { formatBoostProduct } from '~/helpers/format'

export default {
  name: 'PredictiveSearchBar',

  components: {
    BoostAnalytics,
    BoostApiCall,
    ResponsiveImage,
    IconSearch,
  },

  props: {
    defaultProducts: {
      type: Array,
      default: () => ([]),
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    includePages: {
      type: Boolean,
      default: false,
    },
    includeCollections: {
      type: Boolean,
      default: true,
    },
    includeProducts: {
      type: Boolean,
      default: true,
    },
    liquid: {
      type: Object,
      default: () => ({
        shop: '',
      }),
    },
    quickAccess: {
      type: Array,
      default: () => ([]),
    },
  },

  data() {
    return {
      analyticsEvent: {},
      choices: '',
      input: '',
      query: '',
      responseData: {
        type: Object,
        default: () => ({
          all_empty: true,
          redirect: '',
          total_product: 0,
        }),
      },
      products: [],
    }
  },

  computed: {

    /**
     * Set to use the 'search/suggest' endpoint for the API.
     */
    endpoint() {
      return 'search/suggest'
    },

    /**
     * Returns address for search results page
     */
    searchResultsPage() {
      if (this.responseData.redirect) {
        return this.responseData.redirect
      } else {
        return `${this.$variable('routes.search')}?q=${this.query}`
      }
    },
  },

  watch: {

    /**
     * Watches response data to update default products.
     */
    responseData() {
      if (this.responseData.total_product == 0) {
        this.products = this.defaultProducts
      }
    },

    /**
     * Sets focus on input when dropdown is opened.
     */
    isActive() {
      if (this.isActive) {
        this.$refs.inputField.focus()
      }
    },
  },

  mounted() {
    this.products = this.defaultProducts
  },

  methods: {

    /**
     * Fetches search results based on input changes.
     * Sets default products if input field is empty
     */
    handleSearch() {
      if (!this.input) {
        this.query = ''
        this.products = this.defaultProducts
        return
      }

      this.query = this.input
    },

    /**
     * Redirects browser to the results page.
     */
    onSubmit() {
      window.location = this.searchResultsPage
    },

    /**
     * Callback for Boost API call.
     * @param {Object} response - Response from API call.
     */
    boostUpdated(response) {
      if (!response || !this.input) {
        return
      }

      this.responseData = response
      this.redirect = response.redirect

      this.products = this.responseData.products.map((product) => {
        if (!product) {
          return
        }

        return formatBoostProduct(product)
      })
    },

    /**
     * Pass data from analytics events to analytics component.
     * @param {String} productId - Product ID to string.
     * @param {Event} event - Click event.
     */
    handleAnalyticsEvent(productId) {
      this.analyticsEvent = {
        pid: productId,
        u: 'view_product',
      }
    },
  },
}
</script>

<style lang="scss">
@import './predictive-search';
</style>
